import { useAtomValue, useSetAtom } from "jotai";
import React, { forwardRef, useMemo, useState, useRef, useCallback } from "react";
import { publishTemplate, getThemes } from "../services/Jobs";
import useToast from "@/hooks/useToast";
import { useRouter } from "next/router";
import Searchbar from "@/components/searchBox/searchBox";
import { css } from "@emotion/react";
import { Icon } from "@/modules/ui-elements/Icon";
import ProjectNameSection from "src/modules/editor-v2/ConfirmJobPopUpV2/ProjectNameSection";
import CustomizedButton from "@/modules/ui-elements/CustomizedButton";
import { useSelector } from "react-redux";
import useEditorService from "@/hooks/useEditorService";
import { pagesAtom, activeProjectAtom, pageStatesAtom, standardInfographic, leftNavigationActiveIndexAtom, layersAtom } from "../Atoms";
import DMSCanvas from "editor/src/DMSCanvas";
import SecondaryButton from "@/modules/ui-elements/SecondaryButton/index";
import { ICONS } from "@/modules/ui-elements/Icon";
import PopUp from "@/modules/ui-elements/PopUp";
import useAuth from "@/hooks/useAuth";
import { zipAndDownloadImages } from "../ConfirmJobPopUpV2";
import CropImageIcon from '../ConfirmJobPopUpV2/cropImageIcon.svg';
import CropImageView from "../ConfirmJobPopUpV2/CropImageView";
import CheckboxFilledIcon from './publishCheckboxFilled.svg';
import CheckboxIcon from './publishCheckbox.svg';
import { Tooltip } from "react-tooltip";
import { DEAFULT_WORK_TYPE_LIST } from "../constants";
import { uniqBy } from "lodash";

const AlertType = {
    Theme: 'Theme',
    Worktype: 'Worktype',
    All: 'All'
}

const PublishPopUp = forwardRef((props, ref) => {
    const pageStates = useAtomValue(pageStatesAtom);
    const pages = useAtomValue(pagesAtom);
    const toaster = useToast();
    const activeProject = useAtomValue(activeProjectAtom);
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingDownload, setLoadingDownload] = useState(false)
    const { entireStrapiData } = useSelector((state) => state.strapi)
    const strapiData = entireStrapiData?.StudioProjectDetailsNew?.StudioProjectDetailsNew;
    const { push } = useRouter();
    const [isOpen, setIsOpen] = React.useState(false);
    const disabled = useMemo(() => !!(loading || loadingDownload), [loading]);
    const { disposeCanvas, destroy, activeEditor } = useEditorService();
    const setActiveProject = useSetAtom(activeProjectAtom);
    const [themes, setThemes] = useState('');
    const [worktypeList, setWorktypeList] = useState(entireStrapiData?.SelectWorkType?.SelectWorkType || DEAFULT_WORK_TYPE_LIST)
    const [selectedTheme, setSelectedTheme] = useState([]);
    const [updatedTheme,setUpdatedTheme] = useState([]);
    const [selectedWorkTypes, setSelectedWorkTypes] = useState([]);
    const [themeDropDownStatus, setThemeDropdownStatus] = useState(false);
    const [workTypeDropDownStatus, setworkTypeDropdownStatus] = useState(false);
    const isStandardInfographic = useAtomValue(standardInfographic);
    const [alert, setAlert] = useState(null);
    const successPopUpRef = useRef(null);
    const setActiveLeftNavigationIndex = useSetAtom(leftNavigationActiveIndexAtom);
    const {user} = useAuth();
    const layers = useAtomValue(layersAtom);
    const isInfoGraphicUser = useMemo(()=> user?.user?.infoGraphicsUser || false,[user?.entity?.entity?._id]);
    const {clearGrid} = useEditorService();
    const handlePublishCloseClick = () => {
        setActiveLeftNavigationIndex(-2);
        disposeCanvas();
        setActiveProject(undefined);
        push('/dashboard/editor');
        return;
    }

    const getAspectRatio = (height, width) => {
        if(pages?.[0]?.canvas?.width/pages?.[0]?.canvas?.height > 1){
            return { name: "Landscape", icon: ICONS.landscapeDark}
        }
        else if(pages?.[0]?.canvas?.width/pages?.[0]?.canvas?.height < 1){
            return { name: "Portrait", icon: ICONS.portraitDark}
        }else {
            return { name: "Square", icon: ICONS.squareDark}
        }
    }
    const onConfirm = async () => {
        try {
            setLoading(true);
            if (!activeProject) throw new Error(strapiData?.publishEmptyProjectText || "Tried to publish template when no Project was active.");
            if (isStandardInfographic) {
                if(!selectedTheme?.length && !selectedWorkTypes?.length) {
                    setAlert(AlertType.All)
                    setLoading(false);
                    return;
                }
                if(!selectedTheme?.length) { 
                    setAlert(AlertType.Theme);
                    setLoading(false);
                    return;
                }
                if(!selectedWorkTypes?.length) {
                    setAlert(AlertType.Worktype)
                    setLoading(false);
                    return;
                }
            }
            const themesList = selectedTheme?.map((theme)=> theme.ThemeName)
            const workTypeList = selectedWorkTypes?.map((worktype) => worktype.WorktypeName)
            const { job_id: jobId, pre_exist: preExist, guideline } = activeProject;
            const selectedTemplates = pageStates.map(x => ({ image_id: x.imageId }))
            {
                isStandardInfographic 
                    ? await publishTemplate(jobId, selectedTemplates, preExist, guideline, isStandardInfographic, themesList,workTypeList)
                    : await publishTemplate(jobId, selectedTemplates, preExist, guideline, isStandardInfographic)
            };
            setLoading(false);
            setIsOpen(false)
            resetData();
            successPopUpRef.current.open();
            return;
        }
        catch (e) {
            toaster.error("Please try again");
            console.error(e);
            setLoading(false);
        }
    }

    const onDownloadConfirm = async () => {
            if(disabled) return;
            let key;
            try {
                key = toaster.info("Downloading job...",60000);
                setLoadingDownload(true);
                zipAndDownloadImages(images,activeProject?.name, user);
                
                toaster.close(key);
                toaster.success("Download Successfully");

                setLoadingDownload(false);

            } catch(e) {
                console.error(e, "errorr");

                if(e?.response?.data?.message) toaster.error(e.response.data.message);
                else toaster.error("Something went wrong");

                toaster.close(key);
                setLoadingDownload(false);
            } finally{
                setLoadingDownload(false);
            }
    }

    const resetData = () => {
        setworkTypeDropdownStatus(false);
        setThemeDropdownStatus(false);
        setSelectedTheme([])
        setSelectedWorkTypes([])
    }

    const onCloseIconClick = (() => {
        setIsOpen(false);
        setworkTypeDropdownStatus(false);
        setThemeDropdownStatus(false);
    });

    const workTypeDropDown = ((worktype) => {
        const exists = !!selectedWorkTypes.some((existingWorktype) => existingWorktype?.id === worktype?.id)
        if(exists) {
            const filtered = selectedWorkTypes?.filter((type)=> type.id !== worktype.id)
            setSelectedWorkTypes(filtered)
        } else setSelectedWorkTypes(prev => uniqBy([...prev, worktype],"id"))
        setAlert(null)
    })

    const themeDropDown = ((theme) => {
        const exists = !!selectedTheme.some((existingTheme) => existingTheme?.id === theme?.id)
        if(exists) {
            const filtered = selectedTheme?.filter((_theme)=> _theme.id !== theme.id)
            setSelectedTheme(filtered)
        } else setSelectedTheme(prev => uniqBy([...prev, theme],"id"))
        setAlert(null);
    })

    const onWorkTypeSearch = (value) => {
        const staticList = entireStrapiData?.SelectWorkType?.SelectWorkType || DEAFULT_WORK_TYPE_LIST
        if(value){
            const searchList = staticList.filter((obj)=> obj.WorktypeName ? obj?.WorktypeName?.toLowerCase()?.includes(value?.toLowerCase()) : obj?.name?.toLowerCase()?.includes(value?.toLowerCase()))
            setWorktypeList(searchList)
        }else{
            setWorktypeList(staticList)
        }
    }

    const onThemeSearch = (value) => {
        if(value){
            const searchList = themes.filter((obj)=> obj.ThemeName.toLowerCase()?.includes(value?.toLowerCase()))
            setUpdatedTheme(searchList)
        }else{
            setUpdatedTheme(themes)
        }
    }

    const setImagesAsync = () => {
        return new Promise(res => {
            clearGrid();
            const editor = new DMSCanvas("hidden-canvas", { height: 500, width: 500 }, { height: 500, width: 500 });
            Promise.all(pages.map((page) => page.save())).then(async objects => {
                const base64Array = [];
                for (let index = 0; index < objects.length; index++) {
                    const object = objects[index];
                    await editor.load(object);
                    base64Array.push(editor.toBase64Url());
                }
                const images = base64Array.map((imageUrl, index) => ({ image_id: pageStates[index].imageId, template_id: pageStates[index].selectedTemplate, selected: true, url: imageUrl }))
                setImages(images);
                res();
            })
        })
    }
    
    const onImageClickGenerator = (index) => () => {
        setImages(images.map((image, _index) => {
            if(_index === index) {
                return {...image, selected: !image.selected}
            }
            return image;
        }));
    }

    React.useImperativeHandle(ref, () => ({
        open: async () => {
            try {
                setIsOpen(true);
                const res = await getThemes();
                setUpdatedTheme(res);
                setThemes(res);
                await new Promise(res => setTimeout(res, 50));
                await setImagesAsync();
            } catch (error) {
                console.error(error)
                setIsOpen(false);
            }
        },
        close: () => {
            setIsOpen(false);
            setImages([]);
            setLoading(false)
        }
    }), [pageStates, pages, activeProject]);

    const onThemeTagClick = (themeClicked) => {
        if(themeClicked?.id){
            const filteredList = selectedTheme?.filter((item)=> item?.id !== themeClicked?.id)
            setSelectedTheme(filteredList)
        }else{
            setSelectedTheme(selectedTheme)
        }
    }

    const onWorktypeTagClick = (worktypeClicked) => {
        if(worktypeClicked?.id){
            const filteredList = selectedWorkTypes?.filter((item)=> item?.id !== worktypeClicked?.id)
            setSelectedWorkTypes(filteredList)
        }else{
            setSelectedWorkTypes(selectedWorkTypes)
        }
    }

    const [selectedCropIndex, setSelectedCropIndex] = useState(null)

    const onCropImageClick = (index) => () => {
        setSelectedCropIndex(index);
    }

    const onCropClose = () => {
        setSelectedCropIndex(null);
    }

    const onCropSuccess = (imageBase64) => {
        let _images = [...images];
        if(!_images[selectedCropIndex]) return;
        _images[selectedCropIndex].url = imageBase64;
        setImages(_images);
        setSelectedCropIndex(null);
    }

    return (<>
        <div css={styles.backdrop(isOpen)} onClick={onCloseIconClick} ></div>
        <div css={styles.popUp(isOpen)}>
        {(Number.isInteger(selectedCropIndex)) ? <CropImageView image={images?.[selectedCropIndex]?.url || ""} onCropClose={onCropClose} onCropSuccess={onCropSuccess} />
        :<>
            <div className="header">
                <div className="left-section">
                    {"Design will be published for Custom user"}&nbsp;<b>{` "${isStandardInfographic ? "Standard": user?.entity?.entity?.company_name}"`}</b>
                </div>
                <div className="right-section">
                    <Icon src={"/icons/close.svg"} onClick={onCloseIconClick} height={24} width={24} style={styles.closeIconStyle} />
                </div>
            </div>
            <div className="divider"></div>
            <div className="popup-row">
                <div 
                    css={styles.leftCol(images?.length <= 1)} 
                    onClick={()=>{
                        setworkTypeDropdownStatus(false);
                        setThemeDropdownStatus(false);
                    }}
                >
                    <div className="image-list">
                        {
                            images.map((image,index) => (
                                <div className="image-item">
                                    <img src={image.url} css={styles.image(images.length)} />
                                    {
                                        (isInfoGraphicUser && !isStandardInfographic) && 
                                            <>
                                                <div onClick={onImageClickGenerator(index)}  style={styles.checkbox}>
                                                    { image.selected 
                                                        ? <CheckboxFilledIcon />
                                                        : <CheckboxIcon />
                                                    }
                                                </div>
                                                <button data-tooltip-id={'toolId'} data-tooltip-content={"Crop"} css={styles.cropImageButton} onClick={onCropImageClick(index)} >
                                                    <CropImageIcon />
                                                    <Tooltip id={'toolId'} style={styles.tooltipStyle} />
                                                </button>
                                            </>
                                    }
                                </div>
                            ))
                        }
                    </div>
                </div>
                <div className="body-container">
                    <div className="body">
                        <ProjectNameSection />
                        {
                            isStandardInfographic && 
                                (
                                    <div css={styles.pt24}>
                                        <div css={styles.themeText}>
                                            {"Format"} 
                                        </div>
                                        <div style={styles.formatView}>
                                            <div style={styles.formatTextWithIconStyle}>
                                                <Icon src={getAspectRatio()?.icon} height="18" width="18" style={{marginRight:8}}/>
                                                { getAspectRatio()?.name }
                                            </div>
                                        </div>
                                        {/* WORKTYPE */}
                                        <div>
                                            <div css={styles.themeText}>
                                                {strapiData?.WorktypeText || "Work-Type"} 
                                            </div>
                                            { 
                                                workTypeDropDownStatus && 
                                                    (
                                                        <div css={styles.themeDropDown}>
                                                            <div css={styles.dropdownHeader}>
                                                                <div css={styles.themeHeader}>
                                                                    {strapiData?.SelectWorktypeText || "Select Work-Type"}
                                                                    <Icon src={ICONS.crossIcon} height="18" width="18" onClick={()=>setworkTypeDropdownStatus(false)} style={styles.closeIconStyle}/>
                                                                </div>
                                                                <div style={{"margin":"10px 10px 10px 10px"}}>
                                                                    <Searchbar placeholderCTA={"Search Worktype"}onChange={onWorkTypeSearch} />
                                                                </div>
                                                            </div>
                                                            <div style={{paddingTop:'100px'}}>
                                                                {
                                                                    worktypeList && worktypeList.map((worktype) => {
                                                                        const selected = selectedWorkTypes?.find((activeItem)=> {
                                                                            return activeItem?.id === worktype?.id
                                                                        })
                                                                        return (
                                                                        <div style={styles.themeTiles}>
                                                                            <SecondaryButton
                                                                                title={worktype.WorktypeName ? worktype.WorktypeName : worktype.name}
                                                                                onMultiSelectClick={()=>{workTypeDropDown(worktype)}}
                                                                                style={selected ? { ...styles.themeDropdownOptions, border:"1px solid #262626" } : styles.themeDropdownOptions}
                                                                                rightIcon={selected ? ICONS.checkboxTicked : ICONS.checkboxUnticked}
                                                                                multiSelect={true}
                                                                            />
                                                                        </div>
                                                                    )})}
                                                            </div>
                                                        </div>
                                                    )
                                            }
                                            <SecondaryButton
                                                type={"black-bg-grey"}
                                                toggleType={"secondary"}
                                                rightIcon={ICONS.arrowDownDark}
                                                title={strapiData?.SelectWorktypeText || 'Select work-type'}
                                                onClick={() => { setworkTypeDropdownStatus(!workTypeDropDownStatus) }}
                                                dropDownStatus={workTypeDropDownStatus}
                                                style={styles.themeOptions}
                                            >
                                            </SecondaryButton>
                                            { (alert === AlertType.Worktype || alert === AlertType.All) 
                                                && <div style={styles.alert}>
                                                    {strapiData?.WorktypeAlert || "*Please select work-type"}
                                                </div> 
                                            }
                                            <div style={styles.tagView}>
                                            {
                                                selectedWorkTypes?.map((item, index)=>{
                                                    return (
                                                        <div 
                                                            style={{...styles.singleTagView, marginLeft: index !==0 ? 8 : 0}} 
                                                            onClick={()=>onWorktypeTagClick(item)}
                                                        >
                                                            <div style={styles.selectedThemeText}>
                                                                {item?.WorktypeName ? item?.WorktypeName : item?.name}
                                                            </div>
                                                            <div style={styles.crossIconStyle}>
                                                                <Icon src={ICONS.crossIcon} height="10" width="10" style={styles.closeIconStyle}/>
                                                            </div>
                                                        </div>
                                                    )
                                                })
                                            }
                                            </div>
                                        </div>
                                        {/* THEMES */}
                                        <div>
                                            <div css={styles.themeText}>
                                                {strapiData?.themeText || "Theme"} 
                                            </div>
                                            { 
                                                themeDropDownStatus && 
                                                    (
                                                        <div css={styles.themeDropDown}>
                                                            <div css={styles.dropdownHeader}>
                                                                <div css={styles.themeHeader}>
                                                                    {strapiData?.selectThemeText || "Select Theme"}
                                                                    <Icon 
                                                                        src={ICONS.crossIcon}
                                                                        height="18"
                                                                        width="18"
                                                                        onClick={()=>setThemeDropdownStatus(false)}
                                                                        style={styles.closeIconStyle}
                                                                    />
                                                                </div>
                                                                <div style={{"margin":"10px 10px 10px 10px"}}>
                                                                    <Searchbar 
                                                                        placeholderCTA={"Search Theme"}
                                                                        onChange={onThemeSearch} 
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div style={{marginTop:'102px'}}>
                                                                {
                                                                    updatedTheme && updatedTheme.map((theme) => {
                                                                        const selected = selectedTheme?.find((activeItem)=> {
                                                                            return activeItem?.id === theme?.id
                                                                        })
                                                                        return (
                                                                            <div style={styles.themeTiles}>
                                                                                <SecondaryButton
                                                                                    title={theme.ThemeName}
                                                                                    onMultiSelectClick={()=>{ themeDropDown(theme)}}
                                                                                    style={selected ? {...styles.themeDropdownOptions, border:"1px solid #262626"} : styles.themeDropdownOptions}
                                                                                    rightIcon={selected ? ICONS.checkboxTicked : ICONS.checkboxUnticked}
                                                                                    multiSelect={true}
                                                                                />
                                                                            </div>
                                                                        )
                                                                    })
                                                                }
                                                            </div>
                                                        </div>
                                                    )
                                            }
                                            <SecondaryButton
                                                type={"black-bg-grey"}
                                                toggleType={"secondary"}
                                                rightIcon={ICONS.arrowDownDark}
                                                title={selectedTheme?.ThemeName ? selectedTheme?.ThemeName : 'Select a Theme'}
                                                onClick={() => { setThemeDropdownStatus(!themeDropDownStatus) }}
                                                dropDownStatus={themeDropDownStatus}
                                                style={styles.themeOptions}
                                            >
                                            </SecondaryButton>
                                            { (alert === AlertType.Theme || alert === AlertType.All) 
                                                && <div style={styles.alert}>
                                                    {strapiData?.themeAlert || "*Please select theme"}
                                                </div> 
                                            }
                                            <div style={styles.tagView}>
                                            {
                                                selectedTheme?.map((item, index)=>{
                                                    return (
                                                        <div 
                                                            style={{...styles.singleTagView, marginLeft: index !==0 ? 8 : 0}} 
                                                            onClick={()=>onThemeTagClick(item)}
                                                        >
                                                            <div style={styles.selectedThemeText}>
                                                                {item?.ThemeName}
                                                            </div>
                                                            <div style={styles.crossIconStyle}>
                                                                <Icon src={ICONS.crossIcon} height="10" width="10"/>
                                                            </div>
                                                        </div>
                                                    )
                                                })
                                            }
                                            </div>
                                        </div>
                                    </div>
                                )
                        }
                </div>
                <div css={styles.bottomCTAs}>
                    <CustomizedButton 
                        isLoading={loading} 
                        disabled={loading} 
                        btnText={strapiData?.publish || 'Publish'} 
                        type={'pink_with_border'} 
                        isActive={true} 
                        btnStyle={styles.btnStyle(disabled)} 
                        onClick={onConfirm}  
                        startIcon={ICONS.WhitePublish}
                    />
                    <CustomizedButton 
                        btnText={strapiData?.DownloadCTA || "Download"} 
                        type={'secondary'} 
                        startIcon={ICONS.downloadDark} 
                        isActive={true} 
                        btnStyle={loadingDownload ? styles.disabledDownloadBtn:styles.downloadBtn} 
                        btnTextStyle={loadingDownload ? styles.disabledBtnText: styles.downloadBtnText} 
                        onClick={onDownloadConfirm} 
                        isLoading={loadingDownload} 
                        disabled={loadingDownload}
                    />
                </div>
                </div>
                <div style={styles.hiddenCanvas}>
                    <canvas id="hidden-canvas" height={500} width={500}></canvas>
                </div>
            </div>
        </>}
        </div>
        <PopUp
            ref={successPopUpRef}
            width={520}
            disableBackdropClick={true}
            showCloseButton={true}
            onCloseHandler={handlePublishCloseClick}
        >
            <div css={styles.successContainer}>
                <img src="/icons/greenTick.svg" />
                <div>{activeProject?.name}</div>
                <div>{strapiData?.publishedSuccess || "Published Successfully"}</div>
            </div>
        </PopUp>
    </>
    );
});

const styles = {
    themeTiles: { margin: "10px", borderRadius: "4px" },
    hiddenCanvas: {
        position: 'absolute',
        opacity: 0,
        pointerEvents: 'none',
        top: 0,
        left: 0
    },
    successContainer: css`
        width: 382px;
        height: 160px;
        top: 64px;
        left: 69px;
        border-radius: 8px;
        border: 1px solid rgba(75, 181, 67, 1);
        color: rgba(75, 181, 67, 1);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        gap: 10px;
        margin: 45px 0px 45px 45px;
        padding: 20px;
        box-shadow: 0px 3px 24px 0px rgba(38, 38, 38, 0.05);
    `,
    themeHeader: css`
        font-family: Objectivity;
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
        letter-spacing: 0em;
        text-align: left;
        height: 40px;
        border: 0.5px solid rgba(238, 238, 238, 1);
        color: black;
        padding: 10px;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
    `,
    themeOptions: {
        fontSize: "12px",
        fontWeight: "400",
        padding: "5px",
        cursor: "pointer",
        fontFamily:'Objectivity-Regular'
    },
    themeDropdownOptions:{
        fontSize: "12px",
        fontWeight: "400",
        cursor: "pointer",
        fontFamily:'Objectivity-Regular',
        backgroundColor:'#F9F9F9',
        borderRadius:4,
        color:"#262626",
        padding:"8px 8px 8px 8px"
    },
    alert: {
        color: "red",
        fontSize: "12px",
    },
    themeStyle: { padding: "5px" },
    btnStyle: (disabled) => css`
        border-radius: 8px ,
        padding: 16px 0 ,
        font-weight:400,
        border: none ,
        opacity:${disabled ? 0.6 : 1},
    `,
    backdrop: (visible) => css`
        position:fixed;
        top:0px;
        left:0px;
        right:0px;
        bottom:0px;
        background:rgba(0, 0, 0, 0.4);
        z-index:10000;
        height:100vh;
        width:100vw;
        transition:all 0.5s ease-in-out;
        visibility:${visible ? "visible" : "hidden"};
        opacity:${visible ? "1" : "0"};
    `,
    popUp: (visible) => css`
        display:flex;
        flex-direction: column;
        position:fixed;
        top:50%;
        left:50%;
        transform:translate(-50%,-50%);
        width: calc(100vw - 488px);
        min-width:918px;
        max-width:940px;
        z-index:10000000;
        color:grey;
        background:white;
        visibility:${visible ? "visible" : "hidden"};
        transition:all 0.25s ease;
        opacity:${visible ? "1" : "0"};
        border-radius: 8px 8px 8px 8px;
        .header {
            display:flex;
            flex-direction:row;
            justify-content:space-between;
            align-items:center;
            padding:16px 24px;
        }
        .header .left-section {
            color: #262626;
            font-family: Objectivity;
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            line-height: 170%;
            display: flex;
            align-items: center;
        }
        .divider {
            display:flex;
            border: 1px solid #EEEEEE;
        }

        .popup-row {
            display:flex;
            flex-direction:row;
            min-height:506px;
            max-height:588px;
        }

        .body-container {
            width: 412px;
            justify-content:space-between;
            display:flex;
            flex-direction:column;
        }

        .body {
            padding:10px 16px;
            flex:1;
        }
        .footer {
            padding:10px 16px;
        }

    `,
    themeDropDown: css`
        box-shadow: 0px 1px 16px 0px rgba(38, 38, 38, 0.05);
        width: 320px;
        height: 450px;
        top: 65px;
        position: absolute;
        background-color: white;
        z-index: 9999;
        right: 5%;
        border-radius: 4px;
        overflow: hidden;
        overflow-y: auto;
        ::-webkit-scrollbar-thumb {
            background: rgba(38, 38, 38, 1);
            opacity:0;
            transition: all 2s;
        }
        ::-webkit-scrollbar {
            width: 6px;
        }
    `,
    leftCol: (single) => css`
        flex:1; 
        flex-direction:column; 
        display:flex;  
        overflow-y:scroll; 
        background:#F9F9F9; 
        border-radius:0px 0px 0px 8px;
        padding:${single ? "24px;" : "10px 24px"};
        ::-webkit-scrollbar-thumb {
            background: #f1f1f100;
            opacity:0;
            transition: all 2s;
        }
        ::-webkit-scrollbar {
            width: 6px;
        }
        :hover {
            ::-webkit-scrollbar-thumb {
                background: #f1f1f1ff;
                opacity:1;
                border-radius:3px;
                
            }
        }
        .image-list { 
            display:flex; 
            flex-direction:row; 
            align-items:center; 
            justify-content:center; 
            gap:8px; 
            margin-bottom:4px; 
            margin-top:4px; 
            flex-wrap:wrap; 
            flex:1;
        }
        .image-item {
            flex:0.5; 
            flex-grow:1; 
            min-width:calc(50% - 12px); 
            position:relative;  
            display:flex; 
            justify-content:center;
        }
    `,
    image: (length) => css`
        ${(length <= 1) ? "max-height:506px;" : "width:100%;"} 
        object-fit:contain;
    `,
    pt24: css`
        padding-top:24px;
    `,
    themeText: css`        
        color: #262626;
        font-family: Objectivity;
        font-size: 12px;
        font-style: normal;
        font-weight: 600;
        line-height: 17px;
        margin-bottom: 8px;
    `,
    selectedThemeText:{
        "font-family": "Objectivity",
        "font-size": "12px",
        "font-weight": "400",
        "line-height": "20.4px",
        "text-align": "left",
        "color": "#262626",
        "overflow": 'hidden', 
        "textOverflow": 'ellipsis', 
        "whiteSpace": 'nowrap'
    },
    disabledDownloadBtn:{
        backgroundColor:'#EEEEEE', 
        marginTop:12,
        border: "1px solid #262626", 
        borderRadius:4,
    },
    downloadBtn:{
        backgroundColor:'white', 
        border: "1px solid #262626", 
        borderRadius:4, 
        marginTop:12
    },
    disabledBtnText:{
        color:'white'
    },
    downloadBtnText:{
        color:'#262626'
    },
    bottomCTAs:{
        margin:"10px 10px 10px 10px"
    },
    tagView:{
        display:'flex', 
        flexDirection:'row', 
        alignItems:'center', 
        paddingTop:8, 
        overflow:'scroll'
    },
    singleTagView:{
        display:'flex', 
        flexDirection:'row', 
        justifyContent:'center', 
        alignItems:'center', 
        border: '1px solid #DEDEDE', 
        borderRadius:999, 
        cursor:"pointer", 
        paddingLeft:6, 
        paddingRight:4
    },
    crossIconStyle:{
        border: '1px solid #DEDEDE', 
        borderRadius:999, 
        padding:2, 
        marginLeft:4, 
        backgroundColor:'#F9F9F9'
    },
    cropImageButton: css`
        display:flex;
        position:absolute;
        bottom:8px;
        right:8px;
        background: #F9F9F9;
        border: 1px solid #EEE;
        padding:4px;
        border-radius:4px;
        cursor:pointer;
        transition: all 0.3s ease-in-out;
        :hover {
            border-color: #CCC;
            transform:scale(1.05);
        }
    `,
    tooltipStyle: {
        display:'flex', 
        zIndex:1, 
        padding:6, 
        alignItems:'center', 
        fontSize:10
    },
    checkbox:{
        cursor: 'pointer',
        position: 'absolute', 
        top:8, 
        right:8
    },
    closeIconStyle:{
        cursor:"pointer"
    },
    formatView:{
        marginBottom:24
    },
    formatTextWithIconStyle:{
        display:"flex",
        backgroundColor:'#F9F9F9', 
        height:"45px", 
        alignItems:'center', 
        padding: "5px", 
        fontSize: "14px", 
        fontFamily:'Objectivity', 
        lineHeight:23, 
        color:'#262626'
    },
    dropdownHeader: css`
        position: fixed;
        z-index: 1000;
        background: white;
        width: 320px;
    `
}
export default PublishPopUp;